import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { ApiHealthStatus } from "../schema/api-health";

/**
 * Colour carries the same meaning everywhere on the page: red is "this is breaking enrichment
 * right now", amber is "answered but useless", grey is "we do not know". Drift is deliberately
 * neutral — a provider adding a field it does not read is not a problem — and rejected input is
 * blue rather than a warning colour, because it points at our data, not at the provider.
 */
const STATUS_CLASSES: Record<ApiHealthStatus | "UNKNOWN", string> = {
	OK: "bg-green-100 text-green-800",
	DRIFT: "bg-slate-100 text-slate-700",
	INPUT_REJECTED: "bg-blue-100 text-blue-800",
	EMPTY: "bg-amber-100 text-amber-800",
	BREAKING: "bg-red-100 text-red-800",
	DOWN: "bg-red-200 text-red-900",
	UNKNOWN: "bg-gray-100 text-gray-500",
};

interface StatusChipProps {
	status: ApiHealthStatus | null;
	className?: string;
}

export const StatusChip: FunctionComponent<StatusChipProps> = ({ status, className = "" }) => {
	const { t } = useTranslation();

	const key = status ?? "UNKNOWN";

	return (
		<span
			className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[key]} ${className}`}
			title={t(`apiHealth.statusHint.${key}`)}
		>
			{t(`apiHealth.status.${key}`)}
		</span>
	);
};
